/**
 * Platform API Keys Contracts
 * API contracts for managing BAT Platform API keys
 * These keys allow developers to integrate with the BAT REST API programmatically
 */

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────

/**
 * Available scopes for Platform API Keys
 * Scopes follow the pattern: `<resource>:<action>`
 */
export const PLATFORM_API_KEY_SCOPES = {
  TRANSACTIONS_READ: 'transactions:read',
  TRANSACTIONS_WRITE: 'transactions:write',
  USERS_READ: 'users:read',
  USERS_WRITE: 'users:write',
  REPORTS_READ: 'reports:read',
  EXPORTS_READ: 'exports:read',
  SETTINGS_READ: 'settings:read',
  SETTINGS_WRITE: 'settings:write',
  WEBHOOKS_READ: 'webhooks:read',
  WEBHOOKS_WRITE: 'webhooks:write',
  ALL: 'all',
} as const

export type PlatformApiKeyScope =
  (typeof PLATFORM_API_KEY_SCOPES)[keyof typeof PLATFORM_API_KEY_SCOPES]

/** All available scope values, for validation and pickers. */
export const AVAILABLE_PLATFORM_API_SCOPES = Object.values(PLATFORM_API_KEY_SCOPES)

// ─────────────────────────────────────────────────────────────
// REQUEST DTOs
// ─────────────────────────────────────────────────────────────

/**
 * Request to create a new Platform API key
 */
export interface CreatePlatformApiKeyRequest {
  name: string
  scopes: PlatformApiKeyScope[]
  expiresIn?: number // Days until expiration (0 = never expires, max 3650)
  rateLimit?: number // Requests per minute (1-10000)
}

/**
 * Request to update a Platform API key (metadata only, not the key value)
 */
export interface UpdatePlatformApiKeyRequest {
  name?: string
  scopes?: PlatformApiKeyScope[]
  rateLimit?: number
  expiresIn?: number | null // null = remove expiration
}

/**
 * Request to regenerate a Platform API key
 */
export interface RegeneratePlatformApiKeyRequest {
  reason?: string
}

/**
 * Query parameters for listing Platform API keys
 */
export interface ListPlatformApiKeysQuery {
  includeRevoked?: boolean
  scope?: PlatformApiKeyScope
  page?: number
  limit?: number
}

// ─────────────────────────────────────────────────────────────
// RESPONSE DTOs
// ─────────────────────────────────────────────────────────────

/**
 * Platform API key response (never includes the actual key)
 */
export interface PlatformApiKeyResponse {
  id: string
  tenantId: string
  name: string
  keyPrefix: string // First 8 characters of the key
  scopes: PlatformApiKeyScope[]
  expiresAt: string | null
  isActive: boolean
  rateLimit: number
  lastUsedAt: string | null
  usageCount: number
  createdAt: string
  updatedAt: string
  revokedAt: string | null
  revokedReason: string | null
}

/**
 * Response when creating a Platform API key (includes the full key, shown only once)
 */
export interface CreatePlatformApiKeyResponse {
  id: string
  tenantId: string
  name: string
  key: string // The full API key - only returned on creation
  keyPrefix: string
  scopes: PlatformApiKeyScope[]
  expiresAt: string | null
  rateLimit: number
  createdAt: string
  message: string
}

/**
 * Response when regenerating a Platform API key
 */
export interface RegeneratePlatformApiKeyResponse {
  oldKeyId: string
  newApiKey: Omit<CreatePlatformApiKeyResponse, 'key' | 'message'>
  key: string // The full new API key
  message: string
}

/**
 * Paginated list response for Platform API keys
 */
export interface PlatformApiKeyListResponse {
  items: PlatformApiKeyResponse[]
  pagination: {
    total: number
    page: number
    limit: number
    hasMore: boolean
  }
}

/**
 * Usage statistics for Platform API keys
 */
export interface PlatformApiKeysStats {
  totalKeys: number
  activeKeys: number
  revokedKeys: number
  totalUsage: number
  mostUsedKey: { id: string; name: string; usageCount: number } | null
  expiringSoon: number // Keys expiring within 7 days
}
